/**
 * translationMemoryCards.ts — PLAT-10850 (epic PLAT-10825, E13)
 *
 * Pure derivation for the Translation Review "TM" tab: turn a language's active
 * translation segments into the list of TM-backed fragment cards the
 * `TranslationMemoryPanel` renders. Extracted from the component so the
 * term-resolution + seek-target logic is unit-testable without a full render
 * (code-review: separation of concerns / testability). No React, no store — the
 * caller passes the resolved inputs.
 */

import { resolveSeekTarget, tmEntryIdsOf } from './changedPhraseKit';
import { resolveSourceText } from './translatedPhraseEditing';

import type { TranslationMemoryEntry } from '@src/repositories/TranslationMemoryEntryRepository';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TranslationSegment } from '@src/types.ts/translation';

/** One applied glossary term behind a TM-backed fragment (resolved best-effort). */
export interface AppliedTerm {
  id: string;
  sourceTerm: string;
  translation: string;
  /** Do-not-translate (E4): the term is kept verbatim (`translation === sourceTerm`). */
  isDnt: boolean;
  /** The entry's collection link (`null` = Default bucket). PLAT-10878. */
  collectionId: string | null;
  /** Resolved collection name; `null` when the id doesn't resolve (Default bucket,
   *  or collections not loaded). The panel shows "Default" for a null id. */
  collectionName: string | null;
}

export interface TmEntryCard {
  /** Stable react key — the fragment's source indices. */
  key: string;
  /** Original-language source text behind the fragment. */
  source: string;
  /** The translated text of the fragment. */
  translated: string;
  /** The applied glossary term(s), resolved from `tmEntryIds` (may be empty when
   *  `entriesById` isn't populated or an id no longer resolves — DS5). */
  terms: AppliedTerm[];
  /** How many entry ids the fragment carries (so we can show a count even when
   *  the term details can't be resolved). */
  tmEntryIdCount: number;
  /** Source `initial_index` to seek to (first seekable word); absent if none. */
  seekIndex: number | undefined;
  /** Transcript time of that word in seconds, for the mm:ss label. */
  seekTs: number | undefined;
  /** All source indices behind the fragment — drives the hover cross-pane highlight. */
  originalIndices: readonly number[];
}

/**
 * Resolve a fragment's backend `tmEntryIds` into the applied glossary terms,
 * best-effort: an id with no loaded entry is skipped (DS5 — the entry store may
 * be empty, or the entry since deleted), so the caller must treat a short list
 * as "couldn't resolve", not "fewer terms".
 *
 * Shared by the TM panel's cards (PLAT-10850/10878) and the translation
 * overlay's membership section (PLAT-10852) so there is one definition of what
 * an applied entry looks like to the reviewer. Pure.
 */
export function resolveAppliedTerms(
  ids: readonly string[] | undefined,
  entriesById: ReadonlyMap<string, TranslationMemoryEntry> | undefined,
  collectionNameById: ReadonlyMap<string, string> = new Map(),
): AppliedTerm[] {
  const terms: AppliedTerm[] = [];
  for (const id of ids ?? []) {
    const entry = entriesById?.get(id);
    if (!entry) continue;
    terms.push({
      id,
      sourceTerm: entry.sourceTerm,
      translation: entry.translation,
      isDnt: entry.translation === entry.sourceTerm,
      collectionId: entry.collectionId,
      collectionName: entry.collectionId
        ? (collectionNameById.get(entry.collectionId) ?? null)
        : null,
    });
  }
  return terms;
}

/**
 * Build the TM-backed fragment cards for a language, in reading order (document
 * order by source time — TM membership has no edit timestamp to sort by). Each
 * active fragment whose backend `tmEntryIds` is non-empty becomes one card; its
 * `terms` resolve best-effort against `entriesById` (empty when unavailable —
 * the panel then shows a generic label). Pure.
 *
 * A fragment promoted into an entry **this session** also earns a card
 * (`promotedByFragment`, PLAT-10852): the backend only stamps `tmEntryIds` on a
 * re-translate, so gating on those alone left a just-added term missing from the
 * TM tab until the story was re-translated.
 */
export function buildTmCards(
  segments: readonly TranslationSegment[],
  entriesById: ReadonlyMap<string, TranslationMemoryEntry>,
  baseElements: TranscriptElement[],
  collectionNameById: ReadonlyMap<string, string> = new Map(),
  promotedByFragment?: ReadonlyMap<string, string>,
): TmEntryCard[] {
  const byIndex = new Map<number, TranscriptElement>();
  for (const el of baseElements) byIndex.set(el.initial_index, el);

  const cards: TmEntryCard[] = [];
  for (const seg of segments) {
    for (const fragment of seg.fragments) {
      const ids = tmEntryIdsOf(fragment, promotedByFragment);
      if (!ids.length) continue;
      const terms = resolveAppliedTerms(ids, entriesById, collectionNameById);
      const seek = resolveSeekTarget(fragment.originalIndices, byIndex);
      cards.push({
        key: `tm:${fragment.originalIndices.join(',')}`,
        source: resolveSourceText(fragment, baseElements),
        translated: fragment.translatedValue,
        terms,
        tmEntryIdCount: ids.length,
        seekIndex: seek?.index,
        seekTs: seek?.ts,
        originalIndices: fragment.originalIndices,
      });
    }
  }
  cards.sort((a, b) => (a.seekTs ?? Infinity) - (b.seekTs ?? Infinity));
  return cards;
}
